/**
 * Connexion du personnel : email + mot de passe (argon2id côté serveur).
 * Session de 12 h (A-04) — assez pour tenir une soirée de représentation sans
 * se reconnecter à l’entracte.
 */

import type { FormEvent } from 'react'
import { useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router'

import { messageErreur } from '../lib/api'
import { accueilStaff, CHEMINS, lireSuite } from '../lib/chemins'
import { useTheatre } from '../lib/theatre'
import { Button } from '../ui/Button'
import { BandeauErreur } from '../ui/EmptyState'
import { FormField } from '../ui/FormField'
import { Input } from '../ui/Input'
import { ChargementPage } from '../ui/Spinner'
import { useAuth } from './useAuth'

export function ConnexionStaff() {
  const { session, chargement, connecterStaff } = useAuth()
  const naviguer = useNavigate()
  const [parametres] = useSearchParams()
  /* Actif : `GET /theatre/publique` n'exige aucune session (elle existe pour
     ça). Il fallait auparavant la couper ici, la route étant réservée à
     `admin` — l'écran de connexion affichait donc « Théâtre » en attendant. */
  const { nom: nomTheatre, logo } = useTheatre()

  const [email, setEmail] = useState('')
  const [motDePasse, setMotDePasse] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  if (chargement) return <ChargementPage libelle="Vérification de la session…" />

  // Déjà connecté : on ne montre pas un formulaire de connexion inutile.
  if (session?.sujet === 'staff') {
    return <Navigate to={lireSuite(parametres, accueilStaff(session.utilisateur.role))} replace />
  }

  async function soumettre(evenement: FormEvent): Promise<void> {
    evenement.preventDefault()
    setErreur(null)
    setEnCours(true)
    try {
      const nouvelle = await connecterStaff(email.trim(), motDePasse)
      const defaut =
        nouvelle.sujet === 'staff' ? accueilStaff(nouvelle.utilisateur.role) : CHEMINS.admin
      await naviguer(lireSuite(parametres, defaut), { replace: true })
    } catch (cause) {
      // Message du backend tel quel : « Identifiants incorrects », « Trop de
      // tentatives, réessayez dans une minute » (rate limit A-02).
      setErreur(messageErreur(cause, 'Connexion impossible.'))
      setMotDePasse('')
    } finally {
      setEnCours(false)
    }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-slate-100 px-4 py-12">
      <div className="w-full max-w-sm">
        <header className="mb-6 text-center">
          {logo && (
            <img
              src={logo}
              alt={nomTheatre}
              className="mx-auto mb-4 max-h-20 max-w-48 object-contain"
            />
          )}
          <p className="text-sm font-medium tracking-wide text-rideau-800 uppercase">
            {nomTheatre}
          </p>
          <h1 className="mt-1 text-xl font-semibold text-slate-900">Espace personnel</h1>
        </header>

        <form
          onSubmit={(evenement) => {
            void soumettre(evenement)
          }}
          className="flex flex-col gap-4 rounded-carte border border-slate-200 bg-white p-6 shadow-sm"
          noValidate
        >
          {erreur !== null && <BandeauErreur message={erreur} />}

          <FormField label="Adresse email" requis>
            {(champ) => (
              <Input
                {...champ}
                type="email"
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(evenement) => {
                  setEmail(evenement.target.value)
                }}
                /* ★ EVT-04 — un exemple d'adresse n'a pas à supposer un
                   théâtre : c'est la même convention que les autres champs
                   d'email du produit (`ConnexionAbonne`, `PageSuperadmins`). */
                placeholder="prenom.nom@exemple.ch"
              />
            )}
          </FormField>

          <FormField label="Mot de passe" requis>
            {(champ) => (
              <Input
                {...champ}
                type="password"
                autoComplete="current-password"
                value={motDePasse}
                onChange={(evenement) => {
                  setMotDePasse(evenement.target.value)
                }}
              />
            )}
          </FormField>

          <Button type="submit" chargement={enCours} pleineLargeur taille="lg">
            Se connecter
          </Button>
        </form>

        {/* `slate-600` : à 12 px sur le fond teinté de l’écran de connexion,
            `slate-500` ne mesure que 4,35:1 — sous le minimum AA de 4,5:1. */}
        {/* ★★ EVT-04 — LE PREMIER ÉCRAN QU'UNE ORGANISATRICE VOIT, et il parlait
            d'un théâtre. Aucun lexique ici : cet écran est rendu AVANT toute
            session, et `GET /theatre/publique` peut n'avoir pas répondu — la
            phrase doit être vraie sans rien savoir du tenant. « Votre
            administrateur » l'est, et ne perd rien au passage : c'est bien la
            personne à qui l'on s'adresse qui est désignée. */}
        <p className="mt-6 text-center text-xs text-slate-600">
          Mot de passe oublié ? Les comptes du personnel sont créés et réinitialisés par votre
          administrateur.
        </p>
      </div>
    </main>
  )
}
